import type { Meta, StoryObj } from "@storybook/web-components";
import { withActions } from "@storybook/addon-actions/decorator";
import { CreateServiceCase, CreateServiceCaseModal, CreateServiceCaseProps } from "./CreateServiceCase";
import { html } from "lit";
import { NOTIFICATION_TYPES } from "../src/api/publicApi/createCase/notificationType";
import { CreateCaseResponse } from "../src";

// More on how to set up stories at: https://storybook.js.org/docs/writing-stories
const meta = {
    title: "CreateServiceCase",
    decorators: [withActions],
    component: "mf-create-service-case",
    tags: ["autodocs"],
    render: (args) => CreateServiceCase(args),
    argTypes: {
        multipleSteps: {
            control: { type: "boolean" }
        },
        fileUpload: {
            control: { type: "boolean" }
        },
        locale: {
            control: { type: "select" },
            options: ["de-DE", "en-US"]
        },
        prefillData: {
            control: { type: "object" }
        },
        onMfServiceCaseSubmit: {
            action: "case-create"
        },
        onMfServiceCaseCancel: {
            action: "case-cancel"
        },
        disableSilentAuth: {
            control: { type: "boolean" }
        },
        env: {
            control: { type: "select" },
            options: ["dev", "qa", "prod"]
        },
        origin: {
            control: { type: "text" },
            description: "Origin application of the request, e.g. 'oseon' or 'MyTRUMPF'"
        }
    }
} satisfies Meta<CreateServiceCaseProps>;

export default meta;
type Story = StoryObj<CreateServiceCaseProps>;
type ModalStory = StoryObj<CreateServiceCaseProps & { modalName: string; open?: boolean }>;

export const OnePage: Story = {
    args: {
        multipleSteps: false,
        fileUpload: true,
        locale: "de-DE",
        disableSilentAuth: false,
        env: "qa"
    }
};

export const MultiStep: Story = {
    args: {
        multipleSteps: true,
        fileUpload: true,
        locale: "en-US",
        disableSilentAuth: false,
        env: "qa"
    }
};

export const WithContactData: Story = {
    args: {
        multipleSteps: false,
        locale: "de-DE",
        prefillData: {
            notificationType: null,
            productData: {
                assetId: null,
                errorNumbers: []
            },
            softwareData: {
                productNumber: null,
                softwareModule: null,
                softwareType: null,
                isInstallationProblem: null,
                isLicenseIssue: null,
                softwareProblemClassification: null
            }
        },
        disableSilentAuth: false,
        env: "qa"
    }
};

export const WithMachineStopData: Story = {
    args: {
        multipleSteps: false,
        locale: "de-DE",
        prefillData: {
            notificationType: NOTIFICATION_TYPES.MACHINE_STOP,
            productData: {
                assetId: "TWDEDEMO066",
                errorNumbers: ["1234.5678", "ABCD.1234"]
            },
            applicationTopicIds: [100],
            softwareData: {
                productNumber: "9TWDEDEMOS415",
                softwareModule: "Laser",
                softwareType: "TruTops",
                isInstallationProblem: true,
                isLicenseIssue: true,
                softwareProblemClassification: "fault"
            }
        },
        disableSilentAuth: false,
        env: "dev"
    }
};

export const WithSoftwareData: Story = {
    args: {
        multipleSteps: false,
        locale: "de-DE",
        prefillData: {
            notificationType: NOTIFICATION_TYPES.PROGRAMMING_SOFTWARE,
            softwareData: {
                productNumber: "9TWDEDEMOS415",
                softwareModule: "Laser",
                softwareType: "TruTops",
                isInstallationProblem: false,
                isLicenseIssue: true,
                softwareProblemClassification: "fault"
            },
            description: "This is a prefilled description for the software issue."
        },
        disableSilentAuth: false,
        env: "dev"
    }
};

export const Modal: ModalStory = {
    render: CreateServiceCaseModal,
    args: {
        fileUpload: true,
        locale: "de-DE",
        modalName: "create-service-case",
        env: "qa"
    }
};

export const OpenedModal: ModalStory = {
    render: CreateServiceCaseModal,
    args: {
        fileUpload: true,
        locale: "de-DE",
        modalName: "create-service-case-opened",
        open: true,
        env: "qa"
    }
};

export const DataIngest: ModalStory = {
    render: () =>
        html`<div>
            <button data-trigger-modal="open,create-case-prefill">Open Modal</button>
            <ux-data-ingest prefill-data="#prefill-data">
                <mf-create-service-case-modal
                    modal-name="create-case-prefill"
                    env="dev"
                    locale="de_DE"
                    file-upload
                    origin="MyTRUMPF"
                >
                    <ux-f-sis-create-case-connector slot="connector"></ux-f-sis-create-case-connector>
                </mf-create-service-case-modal>
                <script type="application/json" data-id="prefill-data">
                    {
                        "notificationType": 80,
                        "productData": {
                            "assetId": "900018",
                            "errorNumbers": ["1234.5678", "ABCD.1234"]
                        },
                        applicationTopicIds: [2]
                    }
                </script>
            </ux-data-ingest>
        </div>`
};

const mockResponseOutsideOfServiceHours: CreateCaseResponse = [
    {
        callId: "4afd29ee-082f-475b-a310-afc634896293",
        sisCaseId: "8609005",
        transactionState: "Received",
        isCustomerOrderNumberRequired: false,
        isOutsideOfServiceHours: true,
        technicalServiceHotline: "+49715630333222",
        serviceHours: {
            MonFriStart: "05:30",
            MonFriEnd: "16:00"
        }
    }
];
const mockResponseDuringServiceHours: CreateCaseResponse = [
    {
        callId: "a3f7186c-fa36-4cd4-a86f-0e8826c1d8fe",
        sisCaseId: "8608710",
        transactionState: "Received",
        isCustomerOrderNumberRequired: false,
        technicalGuideAvailable: false,
        isOutsideOfServiceHours: false,
        serviceHours: {
            MonFriStart: "05:30",
            MonFriEnd: "15:30"
        }
    }
];

export const CreateCaseSuccess = {
    render: () =>
        html`<mf-create-service-case-success
            locale="de-DE"
            .response=${mockResponseDuringServiceHours}
            my-trumpf-base-url="https://trumpf.com.cms.qa.tru"
        ></mf-create-service-case-success>`
};

export const CreateCaseSuccessOutsideOfServiceHours = {
    render: () =>
        html`<mf-create-service-case-success
            locale="de-DE"
            .response=${mockResponseOutsideOfServiceHours}
            my-trumpf-base-url="https://trumpf.com.cms.qa.tru"
        ></mf-create-service-case-success>`
};
